// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, Grid2, Tooltip, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseLabelValueField, BaseTable, Loading } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IHeaderCellType } from '~/utils/interface/common';
import { DEFAULT_TOOLTIP_DELAY, EMPTY_STRING } from '~/utils/constants/common';
import { IService } from '~/utils/interface/service';
import { IMaterial } from '~/utils/interface/material';
import { IPriceBookDetail } from '~/utils/interface/priceBook';
import { getPriceBookDetail } from '~/thunks/priceBook/priceBookThunk';
import { mockPriceBookDetail } from '~/utils/mockData/priceBook';
import { formatDecimal } from '~/utils/helper/common';
// Styles, images, icons
import styles from './PriceBookInformation.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const serviceColumns = (t: TFunction): IHeaderCellType<IService>[] => {
  return [
    {
      id: 'name',
      label: t('table_head_title_name'),
      render: (record: IService) => record?.name ?? EMPTY_STRING,
    },
    {
      id: 'taskCode',
      label: t('table_head_title_task_code'),
      render: (record: IService) => record.taskCode ?? EMPTY_STRING,
    },
    {
      id: 'price',
      label: t('table_head_title_price'),
      render: (record: IService) => formatDecimal(Number(record.price)) ?? EMPTY_STRING,
    },
    {
      id: 'unit',
      label: t('table_head_title_unit'),
      render: (record: IService) => record.unit ?? EMPTY_STRING,
    },
    // {
    //   id: 'quantity',
    //   label: t('table_head_title_quantity'),
    //   render: (record: IService) => record.quantity ?? EMPTY_STRING,
    // },
  ];
};

const materialColumns = (t: TFunction): IHeaderCellType<IMaterial>[] => {
  return [
    {
      id: 'name',
      label: t('table_head_title_name'),
      render: (record: IMaterial) => record?.name ?? EMPTY_STRING,
    },
    {
      id: 'partNumber',
      label: t('table_head_title_part_number'),
      render: (record: IMaterial) => record.partNumber ?? EMPTY_STRING,
    },
    {
      id: 'price',
      label: t('table_head_title_price'),
      render: (record: IMaterial) => formatDecimal(Number(record.price)) ?? EMPTY_STRING,
    },
    {
      id: 'unit',
      label: t('table_head_title_unit'),
      render: (record: IMaterial) => record.unit ?? EMPTY_STRING,
    },
    {
      id: 'quantity',
      label: t('table_head_title_quantity'),
      render: (record: IMaterial) => record.quantity ?? EMPTY_STRING,
    },
  ];
};

const PriceBookInformation = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/priceBook']);
  const { id: priceBookId } = useParams();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [priceBookDetail, setPriceBookDetail] = useState<IPriceBookDetail>(mockPriceBookDetail);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!priceBookId) return;
    handleGetPriceBookDetail(priceBookId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBookId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetPriceBookDetail = (priceBookId: string) => {
    setIsLoading(true);

    dispatch(getPriceBookDetail(priceBookId))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        setPriceBookDetail(data);
      })
      .catch((_err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='priceBookInformationPage' className={cx('container')}>
      <Grid2 container spacing={'24px'}>
        <Grid2 size={12}>
          <BaseLabelValueField
            label={t('tab_information_name')}
            value={priceBookDetail?.name ?? EMPTY_STRING}
          />

          <Box component={'div'} className={cx('viewTable')}>
            <Tooltip
              title={t('tab_information_services')}
              placement='top-start'
              enterDelay={DEFAULT_TOOLTIP_DELAY}
              leaveDelay={DEFAULT_TOOLTIP_DELAY}
            >
              <Typography component={'div'} className={cx('tableTitle')}>
                {t('tab_information_services')}
              </Typography>
            </Tooltip>

            <BaseTable columns={serviceColumns(t)} dataSource={priceBookDetail?.services || []} />
          </Box>

          <Box component={'div'} className={cx('viewTable')}>
            <Tooltip
              title={t('tab_information_materials')}
              placement='top-start'
              enterDelay={DEFAULT_TOOLTIP_DELAY}
              leaveDelay={DEFAULT_TOOLTIP_DELAY}
            >
              <Typography component={'div'} className={cx('tableTitle')}>
                {t('tab_information_materials')}
              </Typography>
            </Tooltip>

            <BaseTable columns={materialColumns(t)} dataSource={priceBookDetail?.materials || []} />
          </Box>
        </Grid2>
      </Grid2>
      {isLoading && <Loading />}
    </Box>
  );
};

export default PriceBookInformation;
